import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatAddress } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PropertyDialog } from "@/features/properties/PropertyDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function PropertiesPage() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [companyId, setCompanyId] = useState("all");
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");   // Fix U
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("nombre");
  const [order, setOrder] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setDialogOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const pageSize = 25;
  const { data, isLoading } = useQuery({
    queryKey: ["properties", { debounced, companyId, status, kind, page, sort, order, brandParam }],
    queryFn: async () =>
      (
        await api.get("/properties", {
          params: {
            q: debounced || undefined,
            company_id: companyId === "all" ? undefined : companyId,
            status: status === "all" ? undefined : status,
            tipo: kind === "all" ? undefined : kind,
            brand: brandParam,
            page,
            page_size: pageSize,
            sort,
            order,
          },
        })
      ).data,
  });

  const { data: companies } = useQuery({
    queryKey: ["companies-options"],
    queryFn: async () => (await api.get("/companies", { params: { page_size: 200 } })).data,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, next }) =>
      (await api.patch(`/properties/${id}/status`, null, { params: { status: next } })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["properties"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const columns = [
    {
      key: "nombre", label: t("app.name"), sortable: true,
      render: (r) => (
        <span className="font-medium">
          {r.nombre}
          {r.tipo && r.tipo !== "edificio" && (
            <span data-testid={`property-kind-${r.id}`}
              className="ml-2 rounded-[4px] border border-line-strong px-1.5 py-[1px] text-[10.5px] font-medium text-ink-secondary">
              {t(`properties.kinds.${r.tipo}`)}
            </span>
          )}
          {r.piloto && (
            <span data-testid={`property-pilot-${r.id}`}
              className="ml-2 rounded-[4px] border border-brand px-1.5 py-[1px] text-[10.5px] font-semibold text-brand">
              {t("properties.pilot_tag")}
            </span>
          )}
        </span>
      ),
    },
    { key: "company_nombre", label: t("properties.company"), width: 190 },
    {
      key: "direccion",
      label: t("app.address"),
      render: (r) => <span className="text-ink-tertiary">{formatAddress(r.direccion)}</span>,
    },
    {
      key: "brand",
      label: t("app.brand"),
      width: 95,
      render: (r) => <StatusChip tone="blue" label={r.brand === "bb" ? "B&B" : "Betos"} />,
    },
    { key: "unidades_count", label: t("properties.units_count"), width: 95, align: "right" },
    { key: "ordenes_abiertas", label: t("properties.open_orders"), width: 110, align: "right" },
    {
      key: "ordenes_vencidas",
      label: t("properties.overdue_orders"),
      width: 95,
      align: "right",
      render: (r) =>
        r.ordenes_vencidas ? (
          <StatusChip tone="red" label={r.ordenes_vencidas} />
        ) : (
          <span className="text-ink-tertiary">0</span>
        ),
    },
    {
      key: "status",
      label: t("app.status"),
      width: 105,
      render: (r) => <StatusChip tone={statusTone(r.status)} label={t(`status.${r.status}`)} />,
    },
    {
      key: "acciones",
      label: "",
      width: 56,
      align: "right",
      render: (r) =>
        can("properties", "update") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`property-actions-${r.id}`}
                onClick={(e) => e.stopPropagation()}
                className="h-7 rounded-[6px] px-2 text-[12px]"
              >
                •••
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-[8px]">
              <DropdownMenuItem
                data-testid={`property-edit-${r.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(r);
                  setDialogOpen(true);
                }}
              >
                {t("app.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`property-toggle-${r.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirm({ id: r.id, next: r.status === "active" ? "inactive" : "active" });
                }}
              >
                {r.status === "active" ? t("app.deactivate") : t("app.activate")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("properties.title")}
        subtitle={t("properties.subtitle")}
        testId="properties-header"
        actions={
          can("properties", "create") && (
            <Button
              data-testid="new-property-button"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
              className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t("properties.new")}
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-white px-6 py-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="properties-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("properties.search")}
            className="h-8 w-[320px] rounded-[6px] pl-8 text-[13px]"
          />
        </div>
        <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setPage(1); }}>
          <SelectTrigger data-testid="properties-company-filter" className="h-8 w-[200px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("properties.company")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            {(companies?.items || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="properties-status-filter" className="h-8 w-[130px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("app.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            <SelectItem value="active">{t("status.active")}</SelectItem>
            <SelectItem value="inactive">{t("status.inactive")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={(v) => { setKind(v); setPage(1); }}>
          <SelectTrigger data-testid="properties-kind-filter" className="h-8 w-[170px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("properties.kind")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("properties.kinds.all")}</SelectItem>
            <SelectItem value="edificio">{t("properties.kinds.edificio")}</SelectItem>
            <SelectItem value="casa">{t("properties.kinds.casa")}</SelectItem>
            <SelectItem value="comercio">{t("properties.kinds.comercio")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="properties-page">
        <DataTable
          testId="properties-table"
          columns={columns}
          rows={data?.items}
          total={data?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          loading={isLoading}
          sort={sort}
          order={order}
          onSortChange={(key) => {
            setSort(key);
            setOrder((o) => (sort === key ? -o : 1));
          }}
          onRowClick={(r) => navigate(`/edificios/${r.id}`)}
          empty={
            <EmptyState
              icon={Building2}
              testId="properties-empty"
              title={t("properties.empty_title")}
              description={t("properties.empty_desc")}
              action={
                can("properties", "create") && (
                  <Button
                    data-testid="properties-empty-new"
                    onClick={() => setDialogOpen(true)}
                    className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                  >
                    {t("properties.new")}
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <PropertyDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        property={editing}
      />
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.next === "inactive" ? t("confirm.deactivate_title") : t("confirm.activate_title")}
        description={confirm?.next === "inactive" ? t("confirm.deactivate_desc") : t("confirm.activate_desc")}
        onConfirm={() => {
          statusMutation.mutate(confirm);
          setConfirm(null);
        }}
      />
    </>
  );
}
